<template>
  <div class="stepper-root">
    <!-- Main layout: progress + form share the left column, preview on the right -->
    <div
      class="step-layout"
      :class="{ 'step-layout--with-preview': showPreview }"
    >
      <div class="main-column">
        <!-- Progress band (aligned with form-pane horizontally) -->
        <div class="progress-band">
          <!-- Narrow screens: one caption line instead of the label row. -->
          <p class="progress-caption">
            Step {{ currentStep + 1 }} of {{ steps.length }} · {{ currentStepMeta.label }}
            <span v-if="currentStepMeta.optional" class="optional-pill">Optional</span>
          </p>
          <div class="progress-bar" aria-hidden="true">
            <div class="progress-fill" :style="{ width: progressPercent + '%' }"></div>
          </div>
          <ol class="step-labels">
            <li
              v-for="(step, index) in steps"
              :key="step.key"
              class="step-label"
              :class="{
                active: index === currentStep,
                done: index < currentStep,
                optional: step.optional,
              }"
              :aria-current="index === currentStep ? 'step' : false"
            >
              <span class="step-number" aria-hidden="true">
                <v-icon v-if="index < currentStep" class="step-icon-check">mdi-check</v-icon>
                <template v-else>{{ index + 1 }}</template>
              </span>
              <span class="step-text">
                {{ step.label }}
                <span v-if="step.optional" class="optional-pill optional-pill--small step-optional-tag">Optional</span>
              </span>
            </li>
          </ol>
        </div>

        <!-- Form pane -->
        <div class="form-pane">
          <v-form ref="formRef" v-model="formValid" validate-on="submit invalid-input">
          <!-- Every step shares the heading so focus can land on it after a
               step change and screen readers announce where the owner is. -->
          <h2 ref="stepHeading" tabindex="-1" class="step-title">
            {{ currentStepMeta.title }}
            <span v-if="currentStepMeta.optional" class="optional-pill">Optional</span>
          </h2>
          <p class="step-lede">{{ currentStepMeta.lede }}</p>

          <!-- Your business (required) -->
          <section v-if="currentStep === STEP.basics" class="step-section">
            <v-text-field
              v-model="formData.adminName"
              label="Your full name"
              variant="outlined"
              density="comfortable"
              :rules="[rules.fullName]"
              autocomplete="name"
              class="mb-3"
            >
              <template #append-inner>
                <v-tooltip location="top" max-width="280">
                  <template #activator="{ props }">
                    <v-icon
                      v-bind="props"
                      icon="mdi-help-circle-outline"
                      size="small"
                      class="field-help-icon"
                      aria-label="Full name help"
                    />
                  </template>
                  <span>{{ hints.adminName }}</span>
                </v-tooltip>
              </template>
            </v-text-field>

            <v-text-field
              v-model="formData.displayName"
              label="Business name"
              variant="outlined"
              density="comfortable"
              :rules="[rules.required]"
              :loading="businessIdChecking"
              :error-messages="businessIdErrorMessages"
              :hint="businessNameHint"
              persistent-hint
              class="mb-3"
            >
              <template #append-inner>
                <v-icon
                  v-if="businessNameIcon"
                  :icon="businessNameIcon"
                  :color="businessNameIconColor"
                  size="small"
                  class="mr-1"
                  :aria-label="businessNameIconLabel"
                />
                <v-tooltip location="top" max-width="280">
                  <template #activator="{ props }">
                    <v-icon
                      v-bind="props"
                      icon="mdi-help-circle-outline"
                      size="small"
                      class="field-help-icon"
                      aria-label="Business name help"
                    />
                  </template>
                  <span>{{ hints.displayName }}</span>
                </v-tooltip>
              </template>
            </v-text-field>

            <v-select
              v-model="formData.numberOfLocations"
              :items="locationCountOptions"
              label="Number of locations"
              variant="outlined"
              density="comfortable"
              :rules="[rules.locationCount]"
              hint="The number of sites your business trades from, e.g. 1 for a single shop."
              persistent-hint
              class="mb-3"
              @update:model-value="onLocationsSelected"
            >
              <template #append-inner>
                <v-tooltip location="top" max-width="280">
                  <template #activator="{ props }">
                    <v-icon
                      v-bind="props"
                      icon="mdi-help-circle-outline"
                      size="small"
                      class="field-help-icon"
                      aria-label="Number of locations help"
                    />
                  </template>
                  <span>{{ hints.numberOfLocations }}</span>
                </v-tooltip>
              </template>
            </v-select>

            <!-- Checkout can bill at most 20 locations, so "20+" stops here and
                 hands over to the prefilled Established enquiry. -->
            <div v-if="needsEnquiry" class="enquiry-note" role="status">
              <v-icon class="enquiry-note-icon">mdi-information-outline</v-icon>
              <div v-if="enquirySent">
                Thanks - we've got your message and will be in touch by email to get you set up. You can close this page; nothing else is needed from you.
              </div>
              <div v-else>
                Businesses with more than {{ maxSelfServeLocations }} locations are set up on our Established plan with custom pricing, which we arrange with you directly.
                <button type="button" class="inline-link" @click="openEnquiry">Contact us</button>
              </div>
            </div>

            <LogoUploader
              class="mb-4"
              :validation-error="logoMissingError"
              :initial-logo-url="logoPreviewUrl"
              :initial-logo-file-name="logoFile ? logoFile.name : ''"
              @update:logo-file="onLogoFileChanged"
              @logo-error="onLogoError"
            />

            <div class="readonly-email-row">
              <div>
                <div class="readonly-email-label">Account email</div>
                <div class="readonly-email-value">{{ ownerEmail }}</div>
              </div>
              <div class="readonly-email-hint">{{ readOnlyEmailHint }}</div>
            </div>
          </section>

          <!-- Make it yours (optional) -->
          <CustomisationStep
            v-else-if="currentStep === STEP.customise"
            :open-panel="openPanel"
            v-model:branding="brandingSlice"
            v-model:title="formData.title"
            v-model:subtitle="formData.subtitle"
            v-model:qr-headline="formData.qrHeadline"
            v-model:qr-show-logo="formData.qrShowLogo"
            :has-logo="!!logoFile"
            :business-id="formData.businessId"
            :show-aux-validation="brandingValidationShown"
            :preview="previewSpec"
            :inline-preview="smAndDown"
            @update:open-panel="onPanelOpened"
            @update:title="onTitleEdited"
            @update:subtitle="onSubtitleEdited"
            @update:qr-headline="onHeadlineEdited"
            @update:qr-show-logo="onQrShowLogoEdited"
          />

          <!-- Your plan (required) -->
          <PlanStep
            v-else-if="currentStep === STEP.plan"
            :number-of-locations="formData.numberOfLocations"
            v-model:package-plan="formData.packagePlan"
            v-model:billing-period="formData.billingPeriod"
            v-model:agreed="agreedToBusinessTerms"
            v-model:ios-acknowledged="acknowledgedIosOnly"
            :validation-shown="planValidationShown"
            @show-terms="showBusinessTerms = true"
          />

          <v-alert
            v-if="submitError"
            type="error"
            variant="tonal"
            density="compact"
            class="mt-4"
          >
            {{ submitError }}
          </v-alert>
        </v-form>

        <!-- Actions -->
        <div class="stepper-actions">
          <v-btn
            v-if="currentStep > 0"
            variant="outlined"
            class="secondary-btn"
            :disabled="isSubmitting"
            @click="previousStep"
          >
            Back
          </v-btn>
          <v-spacer />
          <v-btn
            v-if="currentStepMeta.optional && !hasOptionalEdits"
            variant="outlined"
            class="secondary-btn"
            @click="skipStep"
          >
            Skip for now
          </v-btn>
          <v-btn
            v-if="currentStep < lastStepIndex"
            class="primary-btn"
            :disabled="continueDisabled"
            @click="nextStep"
          >
            {{ continueLabel }}
          </v-btn>
          <v-btn
            v-else
            class="primary-btn"
            :loading="isSubmitting"
            :disabled="isSubmitting"
            @click="submit"
          >
            Continue to checkout
          </v-btn>
        </div>
      </div>
      </div>

      <!-- Sticky desktop preview (the customise step only) -->
      <aside v-if="showPreview" class="preview-pane preview-pane-desktop">
        <div class="preview-sticky">
          <StepPreview :preview="previewSpec" />
        </div>
      </aside>
    </div>

    <!-- Business Terms Dialog -->
    <v-dialog v-model="showBusinessTerms" max-width="700px" scrollable>
      <v-card>
        <v-card-title class="terms-dialog-title">
          Business terms &amp; conditions
        </v-card-title>
        <v-card-text class="terms-dialog-body">
          <BusinessTermsComponent />
        </v-card-text>
        <v-card-actions class="terms-dialog-actions">
          <v-spacer />
          <v-btn class="primary-btn" @click="showBusinessTerms = false">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Established plan: prefilled message to the Lulu team -->
    <ContactDialog
      v-model="enquiryOpen"
      mode="enquiry"
      :initial-message="enquiryMessage"
      :submitting="enquirySubmitting"
      :error-message="enquiryError"
      :success="enquirySuccess"
      @submit="onEnquirySubmit"
      @cancel="onEnquiryClosed"
      @close="onEnquiryClosed"
    />

    <!-- Submission overlay -->
    <Teleport to="body">
      <div v-if="isSubmitting" class="submit-overlay" role="alert" aria-live="assertive">
        <div class="submit-overlay-card">
          <v-progress-circular indeterminate color="primary" :size="52" :width="4" />
          <div class="submit-overlay-title">Taking you to secure checkout</div>
          <p class="submit-overlay-message">
            This takes a few seconds. Please keep this page open.
          </p>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import {
  ref,
  reactive,
  computed,
  watch,
  nextTick,
  onBeforeUnmount,
  shallowRef,
  markRaw,
} from "vue";
import { useDisplay } from "vuetify";
import BusinessTermsComponent from "@/components/Pages/BusinessTermsComponent.vue";
import RewardsCardPreview from "@/components/Organisms/RewardsCardPreview.vue";
import WebDisplayPreview from "@/components/Organisms/WebDisplayPreview.vue";
import QrCodePreview from "@/components/Organisms/QrCodePreview.vue";
import CustomisationStep from "@/components/Organisms/CustomisationStep.vue";
import PlanStep from "@/components/Organisms/PlanStep.vue";
import ContactDialog from "@/components/Organisms/ContactDialog.vue";
import LogoUploader from "@/components/Molecules/LogoUploader.vue";
import StepPreview from "@/components/Molecules/StepPreview.vue";
import { saveBusinessRegistration } from "@/onboardingHandler.js";
import { sendContactMessage } from "@/emailSender.js";
import { checkBusinessIdExists } from "@/firebase-database.js";
import { track, flushAnalytics } from "@/analytics.js";
import { authStore } from "@/stores/authStore.js";
import {
  required,
  businessIdFormat,
  threshold as thresholdRule,
  locationCount,
  fullName,
} from "@/utils/passwordRules.js";
import { hexToRgb } from "@/utils/colorUtils.js";
import {
  isSelfServePlan,
  buildEstablishedEnquiry,
  MAX_SELF_SERVE_LOCATIONS,
} from "@/utils/pricingPlans.js";
import {
  REGISTRATION_DRAFT_KEY,
  LOGO_DRAFT_KEY,
  serialiseDraft,
  parseDraft,
  encodeLogoDraft,
  decodeLogoDraft,
} from "@/utils/registrationDraft.js";

// Three steps: the required business details, one optional customisation
// step, and the plan. `label` is the progress-band wording; `title` and
// `lede` are the form heading and its one-line intro.
const STEP_DEFINITIONS = [
  {
    key: "basics",
    label: "Your business",
    title: "Tell us about your business",
    lede: "Takes about two minutes. The branding step after this is optional.",
  },
  {
    key: "customise",
    label: "Your branding",
    title: "Your branding",
    optional: true,
    lede:
      "Keep our ready-made defaults, or tweak your colours and wording. Everything except the counter sign headline can be changed later from your dashboard.",
  },
  {
    key: "plan",
    label: "Your plan",
    title: "Your plan",
    lede: "30-day free trial, nothing to pay today, cancel any time.",
  },
];

const STEP_KEYS = STEP_DEFINITIONS.map((step) => step.key);

// Step indices by key, e.g. STEP.customise. Use these rather than numbers so
// adding or removing a step doesn't silently shift the logic.
const STEP = Object.fromEntries(STEP_KEYS.map((key, index) => [key, index]));

// Options for the "Number of locations" dropdown: 1 to 20, then "20+". The
// count never decides the plan - the owner picks that on the last step - but
// plans are billed once per location and checkout can bill at most 20. The
// "20+" option is stored as one more than the cap and goes through the
// Established enquiry instead.
const DEFAULT_LOCATION_COUNT = 1;
const ENQUIRY_LOCATION_COUNT = MAX_SELF_SERVE_LOCATIONS + 1;
const LOCATION_COUNT_OPTIONS = Array.from(
  { length: ENQUIRY_LOCATION_COUNT },
  (_, i) => i + 1
);
const LOCATION_COUNT_ITEMS = LOCATION_COUNT_OPTIONS.map((n) => ({
  title:
    n === ENQUIRY_LOCATION_COUNT ? `${MAX_SELF_SERVE_LOCATIONS}+` : String(n),
  value: n,
}));

// Defaults for the optional step.
const DEFAULT_THRESHOLD = 8;
const DEFAULT_BACKGROUND_COLOR = "#ffffff";
const DEFAULT_FOREGROUND_COLOR = "#000000";

// Live previews keyed by the open customisation panel.
const PREVIEW_COMPONENTS = {
  branding: markRaw(RewardsCardPreview),
  web_page: markRaw(WebDisplayPreview),
  qr_design: markRaw(QrCodePreview),
};

const PREVIEW_DISCLAIMERS = {
  branding:
    "This is a rough mockup for illustrative purposes - your customer's actual rewards card will use native dimensions that may differ from what you see here.",
  web_page: "",
  qr_design:
    "A rough mockup - the QR code pattern shown is decorative. Your real, scannable code will be in your welcome email once setup is complete, ready to save and print.",
};

const mapErrorMessage = (error) => {
  if (!error) return "Something went wrong. Please try again.";
  const raw = String(error.message || error || "").trim();
  if (!raw) return "Something went wrong. Please try again.";
  const lower = raw.toLowerCase();
  if (lower.includes("network") || lower.includes("failed to fetch")) {
    return "We couldn't reach our servers. Check your connection and try again.";
  }
  if (lower.includes("invalid plan")) {
    return "That plan isn't available. Please pick another and try again.";
  }
  if (lower.includes("invalid billing")) {
    return "Please pick a billing period and try again.";
  }
  if (lower.includes("number of locations")) {
    return "Please tell us how many locations you have and try again.";
  }
  if (lower.includes("business data")) {
    return "We couldn't save your business details. Please try again in a moment.";
  }
  if (lower.includes("storage") || lower.includes("logo") || lower.includes("upload")) {
    return "We couldn't upload your logo. Please try again or use a smaller image.";
  }
  if (lower.includes("stripe") || lower.includes("checkout")) {
    return "We couldn't start your checkout session. Please try again in a moment.";
  }
  if (lower.includes("android") || lower.includes("wallet")) {
    return "We couldn't finish wallet setup. Please try again.";
  }
  return "We hit a snag finishing your setup. Please try again, or contact hello@lulurewards.co.uk if it persists.";
};

// Ordinal suffix (1st, 2nd, 3rd, 11th...). The threshold slider is 6-14 so
// these all resolve to "th", but handle the general case anyway.
const ordinal = (n) => {
  const num = Number(n) || 0;
  const mod100 = num % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${num}th`;
  switch (num % 10) {
    case 1: return `${num}st`;
    case 2: return `${num}nd`;
    case 3: return `${num}rd`;
    default: return `${num}th`;
  }
};

// Default copy for the optional step, worded to suit any kind of business.
// The reward lands on the visit *after* the redeem threshold is reached - so
// a threshold of 8 stamps makes it the 9th visit.
const rewardVisit = (threshold) => ordinal((Number(threshold) || 0) + 1);

const buildDefaultHeadline = (threshold) =>
  `Join our rewards programme - every ${rewardVisit(threshold)} visit you get a treat!`;

const buildDefaultTitle = (displayName) => {
  const name = (displayName || "").trim();
  return name ? `Welcome to ${name} rewards!` : "Welcome to our rewards programme!";
};

const buildDefaultSubtitle = (threshold) =>
  `Join our rewards programme and get a treat on every ${rewardVisit(threshold)} visit.`;

// True for anything the backend can store as a colour (hex or rgb()).
const isUsableColor = (value) => /^rgb\(/.test(String(hexToRgb(value) ?? ""));

// Tooltip copy for the first step's info buttons.
const HINTS = {
  adminName:
    "Your full name (at least 4 letters) - you'll be set up as the owner of this business. This won't be visible publicly.",
  displayName: "Your business name exactly as customers should see it.",
  numberOfLocations:
    "How many shops or sites you trade from. This doesn't decide your plan - you'll choose that on the last step - but your plan is billed once per location. Got more than 20? Pick 20+ and we'll set you up directly.",
};

export default {
  name: "RegistrationStepper",
  components: {
    BusinessTermsComponent,
    CustomisationStep,
    PlanStep,
    ContactDialog,
    LogoUploader,
    StepPreview,
  },
  setup() {
    const ownerEmail = computed(() => authStore.user?.email || "");
    const readOnlyEmailHint = computed(() =>
      ownerEmail.value
        ? `You'll be the owner, signed in as ${ownerEmail.value}.`
        : "You'll be the owner of this business."
    );
    // Below Vuetify's md breakpoint (960px) there's no room for the sticky
    // preview aside, so the customise step shows the preview inline.
    const { smAndDown } = useDisplay();

    const formRef = ref(null);
    const formValid = ref(false);
    const stepHeading = ref(null);
    const currentStep = ref(STEP.basics);
    const lastStepIndex = STEP_DEFINITIONS.length - 1;
    const currentStepMeta = computed(() => STEP_DEFINITIONS[currentStep.value]);

    const formData = reactive({
      // Your business
      businessId: "",
      adminName: "",
      adminEmail: ownerEmail.value || "",
      displayName: "",
      numberOfLocations: DEFAULT_LOCATION_COUNT,
      // Branding / rewards card
      thresholdForRedeeming: DEFAULT_THRESHOLD,
      auxiliaryFieldLabel: "",
      auxiliaryFieldValue: "",
      backgroundColor: DEFAULT_BACKGROUND_COLOR,
      foregroundColor: DEFAULT_FOREGROUND_COLOR,
      // Customer sign-up. The `*Customised` flags track whether the owner has
      // hand-edited a field; while false it auto-follows its default.
      title: buildDefaultTitle(""),
      titleCustomised: false,
      subtitle: buildDefaultSubtitle(DEFAULT_THRESHOLD),
      subtitleCustomised: false,
      // QR code counter sign design (colours inherited from branding)
      qrHeadline: buildDefaultHeadline(DEFAULT_THRESHOLD),
      qrHeadlineCustomised: false,
      qrShowLogo: true,
      // Plan. Picked by the owner on the last step; "" until then.
      billingPeriod: "annual",
      packagePlan: "",
      // Set once the Established enquiry has been sent.
      establishedEnquirySent: false,
      // Derived on submit
      id: "",
      logoImageName: "",
    });

    // Keep adminEmail aligned with the signed-in user (it might be empty
    // on the very first render before authStore resolves).
    watch(ownerEmail, (val) => {
      formData.adminEmail = val || "";
    });

    // Logo (required). LogoUploader validates it against the wallet
    // requirements and reports problems through `logo-error`.
    const logoFile = shallowRef(null);
    const logoPreviewUrl = ref("");
    const logoError = ref("");
    const logoMissingError = ref("");

    const setLogoPreview = (file) => {
      if (logoPreviewUrl.value) URL.revokeObjectURL(logoPreviewUrl.value);
      logoPreviewUrl.value = file ? URL.createObjectURL(file) : "";
    };

    const saveLogoToDraft = async () => {
      if (!ownerEmail.value) return;
      const file = logoFile.value;
      if (!file) {
        try {
          sessionStorage.removeItem(LOGO_DRAFT_KEY);
        } catch (_) {
          // sessionStorage unavailable - nothing to remove.
        }
        return;
      }
      try {
        const payload = await encodeLogoDraft(file, ownerEmail.value);
        sessionStorage.setItem(LOGO_DRAFT_KEY, payload);
      } catch (_) {
        // Quota exceeded or storage unavailable - user will re-upload on
        // refresh. Files are capped at 1.5 MB so this should be rare.
      }
    };

    const onLogoFileChanged = (file) => {
      logoFile.value = file || null;
      setLogoPreview(logoFile.value);
      logoError.value = "";
      if (file) logoMissingError.value = "";
      saveLogoToDraft();
    };

    const onLogoError = (message) => {
      logoError.value = message;
    };

    onBeforeUnmount(() => {
      if (logoPreviewUrl.value) URL.revokeObjectURL(logoPreviewUrl.value);
    });

    // Optional step bookkeeping: which panel is open, which panels the
    // owner opened or edited (reported when they skip the step), and whether
    // anything was actually changed. Once it has, "Skip for now" would keep
    // those edits anyway, so the button gives way to Continue.
    const openPanel = ref("branding");
    const sectionsTouched = new Set();
    const hasOptionalEdits = ref(false);
    // Set when Continue is pressed on the branding step so the optional
    // extras' pairing error can show.
    const brandingValidationShown = ref(false);
    const markTouched = (section) => sectionsTouched.add(section);
    const markEdited = (section) => {
      sectionsTouched.add(section);
      hasOptionalEdits.value = true;
    };

    const onPanelOpened = (panel) => {
      if (!panel || panel === openPanel.value) return;
      openPanel.value = panel;
      markTouched(panel);
      track("businessOnboarding_optional_section_expanded", { section: panel });
    };

    // Two-way bridge between BrandingEditor's `v-model` payload and the
    // flat fields the handler expects.
    const brandingSlice = computed({
      get: () => ({
        displayName: formData.displayName,
        backgroundColor: formData.backgroundColor,
        foregroundColor: formData.foregroundColor,
        thresholdForRedeeming: formData.thresholdForRedeeming,
        auxiliaryFieldLabel: formData.auxiliaryFieldLabel,
        auxiliaryFieldValue: formData.auxiliaryFieldValue,
      }),
      set: (next) => {
        formData.displayName = next.displayName;
        formData.backgroundColor = next.backgroundColor;
        formData.foregroundColor = next.foregroundColor;
        formData.thresholdForRedeeming = next.thresholdForRedeeming;
        formData.auxiliaryFieldLabel = next.auxiliaryFieldLabel;
        formData.auxiliaryFieldValue = next.auxiliaryFieldValue;
        markEdited("branding");
      },
    });

    // Business ID is derived from the display name: NFD-normalised to strip
    // accents, lowercased, and stripped to [a-z0-9]+. Same ID is used for the
    // Storage logo key, the RTDB node, and the customer sign-up URL.
    const deriveBusinessId = (name) =>
      String(name || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");

    // Availability check (debounced 600ms) - fires whenever displayName changes.
    // If the derived ID is taken, we auto-append the current Unix timestamp
    // (seconds) to make it unique. Transparent to the user via the hint
    // line which always shows the final URL.
    const businessIdChecking = ref(false);
    const businessIdAvailable = ref(null);
    const businessIdSuffixed = ref(false);
    // True when the last check threw (offline, Firebase down), so the field
    // can say why Continue isn't going anywhere.
    const businessIdCheckFailed = ref(false);
    let businessIdDebounceTimer = null;
    let businessIdCheckPromise = null;

    const runBusinessIdCheck = async (derived) => {
      businessIdChecking.value = true;
      businessIdCheckFailed.value = false;
      try {
        const baseExists = await checkBusinessIdExists(derived);
        // Race guard: bail if the user kept typing while we were checking.
        if (deriveBusinessId(formData.displayName) !== derived) return;
        if (baseExists) {
          const suffix = Math.floor(Date.now() / 1000);
          formData.businessId = `${derived}${suffix}`;
          businessIdSuffixed.value = true;
          track("businessOnboarding_business_id_taken", {
            business_id: derived,
            resolved_business_id: formData.businessId,
          });
        } else {
          formData.businessId = derived;
          businessIdSuffixed.value = false;
        }
        businessIdAvailable.value = true;
      } catch {
        businessIdAvailable.value = null;
        businessIdCheckFailed.value = true;
      } finally {
        businessIdChecking.value = false;
      }
    };

    watch(
      () => formData.displayName,
      (next) => {
        const derived = deriveBusinessId(next);
        formData.businessId = derived;
        businessIdAvailable.value = null;
        businessIdSuffixed.value = false;
        businessIdCheckFailed.value = false;
        if (businessIdDebounceTimer) clearTimeout(businessIdDebounceTimer);
        if (!derived) return;

        businessIdDebounceTimer = setTimeout(() => {
          businessIdDebounceTimer = null;
          businessIdCheckPromise = runBusinessIdCheck(derived);
        }, 600);
      },
      { immediate: true },
    );

    // Continue pressed before the debounced check has run (or after it
    // failed): run it now rather than silently refusing to move on.
    const ensureBusinessIdChecked = async () => {
      if (businessIdAvailable.value === true) return;
      const derived = deriveBusinessId(formData.displayName);
      if (!derived) return;
      if (businessIdChecking.value && businessIdCheckPromise) {
        await businessIdCheckPromise;
        return;
      }
      if (businessIdDebounceTimer) {
        clearTimeout(businessIdDebounceTimer);
        businessIdDebounceTimer = null;
      }
      businessIdCheckPromise = runBusinessIdCheck(derived);
      await businessIdCheckPromise;
    };

    const businessIdFormatValid = computed(() =>
      /^[a-z0-9]+$/.test(formData.businessId || ""),
    );

    // Surfaces under the Business name field. Empty input stays quiet (the
    // `required` rule handles that). The only hard error is when the typed
    // name has zero usable letters/numbers; everything else is a hint
    // (checking / available / available-with-suffix).
    const businessIdErrorMessages = computed(() => {
      const name = formData.displayName || "";
      if (!name) return [];
      if (!businessIdFormatValid.value) {
        return [
          "Please include at least one letter or number in your business name.",
        ];
      }
      if (businessIdCheckFailed.value && !businessIdChecking.value) {
        return [
          "We couldn't check that name is available. Check your connection and press Continue to try again.",
        ];
      }
      return [];
    });

    const businessNameHint = computed(() => {
      if (!formData.displayName || !businessIdFormatValid.value) return "";
      if (businessIdChecking.value) return "Checking availability…";
      if (businessIdAvailable.value !== true) return "";
      return businessIdSuffixed.value
        ? `That name's taken, so your link will be lulurewards.co.uk/business/${formData.businessId}`
        : `Your link: lulurewards.co.uk/business/${formData.businessId}`;
    });

    const businessNameIcon = computed(() => {
      if (
        businessIdChecking.value ||
        businessIdAvailable.value !== true ||
        !businessIdFormatValid.value
      ) {
        return "";
      }
      return businessIdSuffixed.value ? "mdi-alert-circle" : "mdi-check-circle";
    });

    const businessNameIconColor = computed(() =>
      businessIdSuffixed.value ? "warning" : "green-darken-2",
    );

    const businessNameIconLabel = computed(() =>
      businessIdSuffixed.value
        ? "Business name taken - link will be suffixed"
        : "Business name available",
    );

    onBeforeUnmount(() => {
      if (businessIdDebounceTimer) clearTimeout(businessIdDebounceTimer);
    });

    const rules = {
      required,
      businessIdFormat,
      threshold: thresholdRule,
      locationCount,
      fullName,
    };
    const passes = (rule, value) => rule(value) === true;

    const agreedToBusinessTerms = ref(false);
    const acknowledgedIosOnly = ref(false);
    const showBusinessTerms = ref(false);
    const planValidationShown = ref(false);

    const isCurrentStepValid = computed(() => {
      switch (currentStep.value) {
        case STEP.basics:
          return (
            passes(rules.businessIdFormat, formData.businessId) &&
            businessIdAvailable.value === true &&
            !businessIdChecking.value &&
            passes(rules.fullName, formData.adminName) &&
            passes(rules.required, formData.displayName) &&
            passes(rules.locationCount, formData.numberOfLocations) &&
            !needsEnquiry.value &&
            !!logoFile.value &&
            !logoError.value &&
            !!ownerEmail.value
          );
        case STEP.customise: {
          // Everything here has a default; the one rule is that the extra
          // card line is either filled in fully or left blank.
          const hasAuxLabel = !!(formData.auxiliaryFieldLabel || "").trim();
          const hasAuxValue = !!(formData.auxiliaryFieldValue || "").trim();
          return hasAuxLabel === hasAuxValue;
        }
        case STEP.plan:
          return (
            !!formData.packagePlan &&
            agreedToBusinessTerms.value &&
            acknowledgedIosOnly.value
          );
        default:
          return false;
      }
    });

    // Bar fills to the position of the current step marker: step 1 → 0%,
    // step 2 → 50%, final step → 100%. The current step shows as "in
    // progress" (not yet complete) until the user advances.
    const progressPercent = computed(() => {
      const total = STEP_DEFINITIONS.length;
      if (total <= 1) return 100;
      return Math.round((currentStep.value / (total - 1)) * 100);
    });

    const scrollToTop = () => {
      if (typeof window !== "undefined") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    };

    // Moves to a step and puts focus on its heading so assistive tech
    // announces the change. preventScroll leaves the smooth scroll alone.
    const goToStep = (index) => {
      currentStep.value = index;
      scrollToTop();
      nextTick(() => {
        stepHeading.value?.focus({ preventScroll: true });
      });
    };

    const nextStep = async () => {
      if (formRef.value) {
        try {
          await formRef.value.validate();
        } catch (_) {
          // Swallow - we re-check `isCurrentStepValid` below anyway.
        }
      }
      if (currentStep.value === STEP.basics) {
        // Logo is a non-field input so the v-form validator doesn't cover it.
        if (!logoFile.value) {
          logoMissingError.value = "Please upload a logo to continue.";
        }
        if (needsEnquiry.value) {
          openEnquiry();
          return;
        }
        await ensureBusinessIdChecked();
      }
      if (currentStep.value === STEP.customise) brandingValidationShown.value = true;
      applyOptionalDefaults();
      if (!isCurrentStepValid.value) return;
      if (currentStep.value < lastStepIndex) {
        track("businessOnboarding_step_advanced", {
          step: currentStep.value + 1,
          step_name: STEP_KEYS[currentStep.value],
        });
        goToStep(currentStep.value + 1);
      }
    };

    // "Skip for now" on the optional step: same destination as Continue, but
    // reported separately along with how much of the step was looked at.
    const skipStep = () => {
      if (!currentStepMeta.value.optional || currentStep.value >= lastStepIndex) return;
      applyOptionalDefaults();
      track("businessOnboarding_optional_step_skipped", {
        sections_touched: [...sectionsTouched],
        sections_touched_count: sectionsTouched.size,
      });
      goToStep(currentStep.value + 1);
    };

    const previousStep = () => {
      if (currentStep.value > 0) {
        track("businessOnboarding_step_back", {
          from_step: currentStep.value + 1,
          from_step_name: STEP_KEYS[currentStep.value],
        });
        goToStep(currentStep.value - 1);
      }
    };

    const continueLabel = computed(() => {
      if (currentStep.value !== STEP.basics || !needsEnquiry.value) return "Continue";
      return enquirySent.value ? "Message sent" : "Contact us about Established";
    });

    // Once the Established enquiry has gone, there's nothing more to do on
    // this step until the Lulu team replies.
    const continueDisabled = computed(
      () => currentStep.value === STEP.basics && needsEnquiry.value && enquirySent.value,
    );

    watch(
      () => formData.packagePlan,
      (val) => {
        if (val) {
          track("businessOnboarding_plan_selected", {
            plan: val,
            billing_period: formData.billingPeriod,
          });
        }
      }
    );

    watch(
      () => formData.billingPeriod,
      (val) => {
        track("businessOnboarding_billing_period_changed", {
          billing_period: val,
          plan: formData.packagePlan || null,
        });
      }
    );

    // Keep the default copy in step with the redeem threshold and display
    // name until the owner edits a field themselves, after which we leave
    // their wording alone.
    watch(
      () => formData.thresholdForRedeeming,
      (next) => {
        if (!formData.qrHeadlineCustomised) {
          formData.qrHeadline = buildDefaultHeadline(next);
        }
        if (!formData.subtitleCustomised) {
          formData.subtitle = buildDefaultSubtitle(next);
        }
      },
    );

    watch(
      () => formData.displayName,
      (next) => {
        if (!formData.titleCustomised) {
          formData.title = buildDefaultTitle(next);
        }
      },
    );

    const onHeadlineEdited = () => {
      formData.qrHeadlineCustomised = true;
      markEdited("qr_design");
    };

    const onQrShowLogoEdited = () => {
      markEdited("qr_design");
    };

    const onTitleEdited = () => {
      formData.titleCustomised = true;
      markEdited("web_page");
    };

    const onSubtitleEdited = () => {
      formData.subtitleCustomised = true;
      markEdited("web_page");
    };

    // The customise step is optional: anything left blank (or unusable)
    // falls back to its default, and goes back to following it, so the
    // owner can skip straight through.
    const applyOptionalDefaults = () => {
      if (!isUsableColor(formData.backgroundColor)) {
        formData.backgroundColor = DEFAULT_BACKGROUND_COLOR;
      }
      if (!isUsableColor(formData.foregroundColor)) {
        formData.foregroundColor = DEFAULT_FOREGROUND_COLOR;
      }
      if (!passes(rules.threshold, formData.thresholdForRedeeming)) {
        formData.thresholdForRedeeming = DEFAULT_THRESHOLD;
      }
      if (!(formData.title || "").trim()) {
        formData.titleCustomised = false;
        formData.title = buildDefaultTitle(formData.displayName);
      }
      if (!(formData.subtitle || "").trim()) {
        formData.subtitleCustomised = false;
        formData.subtitle = buildDefaultSubtitle(formData.thresholdForRedeeming);
      }
      if (!(formData.qrHeadline || "").trim()) {
        formData.qrHeadlineCustomised = false;
        formData.qrHeadline = buildDefaultHeadline(formData.thresholdForRedeeming);
      }
    };

    // Established is priced on request, so "20+" locations on the first step
    // hand over to a prefilled message to the Lulu team instead of checkout.
    const enquiryOpen = ref(false);
    const enquirySubmitting = ref(false);
    const enquiryError = ref("");
    const enquirySuccess = ref(false);
    // Stays true after the dialog closes (and across a refresh, via the
    // draft) so the first step can confirm it instead of asking again.
    const enquirySent = computed({
      get: () => !!formData.establishedEnquirySent,
      set: (val) => {
        formData.establishedEnquirySent = !!val;
      },
    });

    const needsEnquiry = computed(
      () => Number(formData.numberOfLocations) > MAX_SELF_SERVE_LOCATIONS
    );

    const enquiryMessage = computed(() =>
      buildEstablishedEnquiry({
        businessName: formData.displayName,
        locations: formData.numberOfLocations,
      })
    );

    const openEnquiry = () => {
      enquiryError.value = "";
      enquirySuccess.value = false;
      enquiryOpen.value = true;
      track("businessOnboarding_enquiry_opened", {
        number_of_locations: Number(formData.numberOfLocations) || null,
      });
    };

    // Fired by the dropdown only, so a restored draft doesn't pop the dialog.
    const onLocationsSelected = (count) => {
      enquirySent.value = false;
      if (Number(count) > MAX_SELF_SERVE_LOCATIONS) openEnquiry();
    };

    const onEnquirySubmit = async (payload) => {
      if (!ownerEmail.value) {
        enquiryError.value =
          "We couldn't find your email address. Please sign in again.";
        return;
      }
      enquirySubmitting.value = true;
      enquiryError.value = "";
      try {
        await sendContactMessage({
          name: (formData.adminName || "").trim() || ownerEmail.value,
          email: ownerEmail.value,
          title: payload.title,
          message: payload.message,
        });
        enquirySuccess.value = true;
        enquirySent.value = true;
        track("businessOnboarding_enquiry_submitted", {
          number_of_locations: Number(formData.numberOfLocations) || null,
        });
      } catch (e) {
        enquiryError.value =
          "We couldn't send your message. Check your connection and try again, or email hello@lulurewards.co.uk.";
        track("businessOnboarding_enquiry_failed", {
          number_of_locations: Number(formData.numberOfLocations) || null,
        });
      } finally {
        enquirySubmitting.value = false;
      }
    };

    const onEnquiryClosed = () => {
      enquirySuccess.value = false;
      enquiryError.value = "";
    };

    // The sticky aside only has room on wide screens; narrower ones get the
    // same preview inline inside the open panel (see CustomisationStep).
    const showPreview = computed(
      () => currentStep.value === STEP.customise && !smAndDown.value,
    );

    const previewSpec = computed(() => {
      const panel = PREVIEW_COMPONENTS[openPanel.value] ? openPanel.value : "branding";
      const logoUrl = logoPreviewUrl.value || null;
      const base = {
        component: PREVIEW_COMPONENTS[panel],
        disclaimer: PREVIEW_DISCLAIMERS[panel],
      };
      if (panel === "web_page") {
        return {
          ...base,
          caption: "Live sign-up page preview",
          props: {
            title: formData.title,
            subtitle: formData.subtitle,
            logoUrl,
            backgroundColor: formData.backgroundColor,
            textColor: formData.foregroundColor,
          },
        };
      }
      if (panel === "qr_design") {
        return {
          ...base,
          caption: "Live counter sign preview",
          props: {
            headline: formData.qrHeadline,
            logoUrl,
            showLogo: formData.qrShowLogo,
            cardBackgroundColor: formData.backgroundColor,
            textColor: formData.foregroundColor,
          },
        };
      }
      return {
        ...base,
        caption: "Live card preview",
        props: {
          displayName: formData.displayName,
          logoUrl,
          backgroundColor: formData.backgroundColor,
          foregroundColor: formData.foregroundColor,
          thresholdForRedeeming: Number(formData.thresholdForRedeeming) || 8,
          auxiliaryFieldLabel: formData.auxiliaryFieldLabel || null,
          auxiliaryFieldValue: formData.auxiliaryFieldValue || null,
        },
      };
    });

    const draftRestored = ref(false);

    const saveDraft = () => {
      if (!ownerEmail.value) return;
      try {
        sessionStorage.setItem(
          REGISTRATION_DRAFT_KEY,
          serialiseDraft({
            email: ownerEmail.value,
            currentStep: currentStep.value,
            formData,
          }),
        );
      } catch (_) {
        // sessionStorage may be unavailable (private mode, quota) - fail silent.
      }
    };

    const loadLogoFromDraft = () => {
      if (!ownerEmail.value) return false;
      let file = null;
      try {
        file = decodeLogoDraft(sessionStorage.getItem(LOGO_DRAFT_KEY), ownerEmail.value);
      } catch (_) {
        file = null;
      }
      if (!file) return false;
      logoFile.value = file;
      setLogoPreview(file);
      return true;
    };

    const loadDraft = () => {
      if (draftRestored.value || !ownerEmail.value) return;
      draftRestored.value = true;
      let draft = null;
      try {
        draft = parseDraft(sessionStorage.getItem(REGISTRATION_DRAFT_KEY), {
          email: ownerEmail.value,
          lastStep: lastStepIndex,
        });
      } catch (_) {
        draft = null;
      }
      if (!draft) return;

      Object.assign(formData, draft.formData);
      // Only keep a location count the dropdown can show: anything above the
      // cap becomes "20+", anything unusable falls back to the default of 1.
      const restoredCount = parseInt(formData.numberOfLocations, 10);
      if (!Number.isInteger(restoredCount) || restoredCount < 1) {
        formData.numberOfLocations = DEFAULT_LOCATION_COUNT;
      } else {
        formData.numberOfLocations = Math.min(restoredCount, ENQUIRY_LOCATION_COUNT);
      }
      // Only keep a plan that can still be bought through checkout. Older
      // drafts derived it from the count and never stored it.
      if (!isSelfServePlan(formData.packagePlan)) formData.packagePlan = "";
      // Restore the logo too. The first step needs the logo and a count
      // checkout can bill, so if either is missing and the owner was past
      // that step, drop them back to it.
      const logoRestored = loadLogoFromDraft();
      const basicsComplete = logoRestored && !needsEnquiry.value;
      currentStep.value = basicsComplete ? draft.currentStep : STEP.basics;
      track("businessOnboarding_draft_restored", {
        restored_step: currentStep.value,
        logo_restored: logoRestored,
      });
    };

    const clearDraft = () => {
      try {
        sessionStorage.removeItem(REGISTRATION_DRAFT_KEY);
        sessionStorage.removeItem(LOGO_DRAFT_KEY);
      } catch (_) {
        // sessionStorage may be unavailable - nothing to clear.
      }
    };

    watch(
      ownerEmail,
      (val) => {
        if (val) loadDraft();
      },
      { immediate: true },
    );

    watch(formData, saveDraft, { deep: true });
    watch(currentStep, saveDraft);

    const isSubmitting = ref(false);
    const submitError = ref("");

    const submit = async () => {
      submitError.value = "";
      planValidationShown.value = true;
      applyOptionalDefaults();
      if (formRef.value) {
        const validation = await formRef.value.validate();
        if (validation && validation.valid === false) return;
      }
      if (!isCurrentStepValid.value) return;
      if (!ownerEmail.value) {
        submitError.value =
          "Your session expired - please sign back in and try again.";
        return;
      }

      isSubmitting.value = true;
      try {
        formData.id = formData.businessId;
        formData.adminEmail = ownerEmail.value;
        formData.logoImageName = `${formData.id}.png`;
        formData.thresholdForRedeeming = Number(formData.thresholdForRedeeming);

        const checkoutUrl = await saveBusinessRegistration(
          { ...formData },
          logoFile.value
        );
        clearDraft();
        // Flush queued analytics before the hard redirect to Stripe so we
        // don't lose the "registration complete" events on navigation.
        flushAnalytics();
        await new Promise((resolve) => setTimeout(resolve, 200));
        window.location.href = checkoutUrl;
      } catch (error) {
        track("businessOnboarding_failed", {
          error_message: error?.message || "unknown",
        });
        submitError.value = mapErrorMessage(error);
      } finally {
        isSubmitting.value = false;
      }
    };

    return {
      // state
      formRef,
      formValid,
      stepHeading,
      formData,
      brandingSlice,
      logoFile,
      logoPreviewUrl,
      logoError,
      logoMissingError,
      onLogoFileChanged,
      onLogoError,
      hints: HINTS,
      readOnlyEmailHint,
      currentStep,
      currentStepMeta,
      lastStepIndex,
      steps: STEP_DEFINITIONS,
      STEP,
      rules,
      locationCountOptions: LOCATION_COUNT_ITEMS,
      maxSelfServeLocations: MAX_SELF_SERVE_LOCATIONS,
      ownerEmail,
      businessIdChecking,
      businessIdErrorMessages,
      businessNameHint,
      businessNameIcon,
      businessNameIconColor,
      businessNameIconLabel,
      agreedToBusinessTerms,
      acknowledgedIosOnly,
      showBusinessTerms,
      planValidationShown,
      isSubmitting,
      submitError,
      showPreview,
      previewSpec,
      smAndDown,
      openPanel,
      hasOptionalEdits,
      brandingValidationShown,
      progressPercent,
      continueLabel,
      continueDisabled,
      needsEnquiry,
      enquiryOpen,
      enquirySubmitting,
      enquiryError,
      enquirySuccess,
      enquirySent,
      enquiryMessage,
      // actions
      nextStep,
      skipStep,
      previousStep,
      submit,
      onPanelOpened,
      onHeadlineEdited,
      onQrShowLogoEdited,
      onTitleEdited,
      onSubtitleEdited,
      onLocationsSelected,
      openEnquiry,
      onEnquirySubmit,
      onEnquiryClosed,
    };
  },
};
</script>

<style scoped>
.stepper-root {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.main-column {
  display: flex;
  flex-direction: column;
  gap: 24px;
  min-width: 0;
  width: 100%;
  max-width: 720px;
  margin-inline: auto;
}

.step-layout--with-preview .main-column {
  max-width: none;
  margin-inline: 0;
}

.progress-band {
  background: var(--lulu-surface);
  border-radius: 16px;
  padding: 18px 22px 14px;
  box-shadow: var(--lulu-card-shadow);
  border: 1px solid var(--lulu-border);
}

/* Shown in place of the label row on narrow screens (see media query). */
.progress-caption {
  display: none;
  margin: 0 0 10px;
  font-size: 13px;
  font-weight: 600;
  color: var(--lulu-text-strong);
}

.progress-bar {
  position: relative;
  height: 6px;
  border-radius: 999px;
  background: rgba(0, 109, 62, 0.12);
  overflow: hidden;
  margin-bottom: 14px;
}

.progress-fill {
  position: absolute;
  inset: 0;
  width: 0;
  background: var(--lulu-header-gradient);
  border-radius: 999px;
  transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.step-labels {
  display: flex;
  justify-content: space-between;
  list-style: none;
  padding: 0;
  margin: 0;
  gap: 8px;
}

.step-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 700;
  color: var(--lulu-text-muted);
  flex: 1 1 0;
  min-width: 0;
}

.step-label.done {
  color: var(--lulu-primary);
}

.step-label.active {
  color: var(--lulu-text-strong);
}

.step-number {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1.5px solid currentColor;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
}

.step-label.done .step-number,
.step-label.active .step-number {
  background: var(--lulu-primary);
  border-color: var(--lulu-primary);
  color: #ffffff;
}

.step-icon-check {
  font-size: 14px !important;
  color: #ffffff !important;
}

/* One line per step; the Optional pill sits inline after the label. */
.step-text {
  display: inline-flex;
  align-items: center;
  line-height: 1.25;
  white-space: nowrap;
}

.step-text .step-optional-tag {
  margin-left: 6px;
}

/* Optional steps not reached yet get a dashed ring, so the required
   path (Your business, Your plan) stands out at a glance. */
.step-label.optional:not(.done):not(.active) .step-number {
  border-style: dashed;
}

@media (max-width: 720px) {
  .progress-band {
    padding: 14px 18px 12px;
  }
  .progress-caption {
    display: block;
  }
  .progress-bar {
    margin-bottom: 0;
  }
  .step-labels {
    display: none;
  }
}

.step-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 32px;
  align-items: start;
}

.step-layout--with-preview {
  grid-template-columns: minmax(0, 1fr) 320px;
  align-items: center;
}

@media (max-width: 959px) {
  .step-layout,
  .step-layout--with-preview {
    grid-template-columns: 1fr;
  }
}

.form-pane {
  background: var(--lulu-surface);
  border-radius: var(--lulu-radius-card);
  box-shadow: var(--lulu-card-shadow);
  padding: 32px 30px 24px;
  border: 1px solid var(--lulu-border);
}

@media (max-width: 600px) {
  .form-pane {
    padding: 20px 16px;
  }
}

.step-section {
  display: flex;
  flex-direction: column;
}

.step-title {
  font-size: 22px;
  font-weight: 800;
  color: var(--lulu-text-strong);
  margin: 0 0 6px;
  letter-spacing: -0.4px;
}

/* Focused programmatically after a step change (tabindex -1), never by
   keyboard, so no ring is needed. */
.step-title:focus {
  outline: none;
}

.step-lede {
  font-size: 14px;
  color: #4a5568;
  margin: 0 0 22px;
  line-height: 1.55;
}

/* Marks the step an owner can skip straight through. */
.optional-pill {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 10px;
  border-radius: 999px;
  background: rgba(0, 109, 62, 0.08);
  border: 1px solid rgba(0, 109, 62, 0.2);
  color: var(--lulu-primary);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  vertical-align: middle;
}

/* Desktop label row only; the mobile caption uses the full-size pill. */
.optional-pill--small {
  padding: 1px 8px;
  font-size: 10px;
}

/* Info note: "20+" locations on the first step. */
.enquiry-note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px 14px;
  margin: -4px 0 16px;
  background: rgba(0, 109, 62, 0.06);
  border: 1px solid rgba(0, 109, 62, 0.15);
  border-radius: 10px;
  font-size: 13px;
  line-height: 1.5;
  color: rgb(0, 85, 48);
}

.enquiry-note-icon {
  color: var(--lulu-primary);
  font-size: 18px !important;
  margin-top: 1px;
}

.inline-link {
  background: none;
  border: none;
  padding: 0;
  font-size: 13px;
  font-weight: 600;
  color: var(--lulu-primary);
  cursor: pointer;
  text-decoration: underline;
  font-family: inherit;
}

.inline-link:hover {
  color: var(--lulu-primary-dark);
}

.readonly-email-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 14px 16px;
  background: var(--lulu-surface-soft);
  border: 1px solid var(--lulu-border);
  border-radius: 10px;
  margin-top: 4px;
}

.readonly-email-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--lulu-text-muted);
  font-weight: 600;
}

.readonly-email-value {
  font-size: 14px;
  color: var(--lulu-text-strong);
  font-weight: 600;
}

.readonly-email-hint {
  font-size: 12px;
  color: var(--lulu-text-muted);
}

.field-help-icon {
  color: var(--lulu-text-muted);
  opacity: 0.7;
  cursor: help;
  transition: color 0.15s, opacity 0.15s;
}

.field-help-icon:hover {
  opacity: 1;
  color: var(--lulu-primary);
}

.preview-pane {
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* Push the preview down by the height of the progress band + grid gap
   above the form pane, so the preview's vertical centre aligns with
   the form pane's centre (not the whole main-column's centre). */
.step-layout--with-preview .preview-pane-desktop {
  padding-top: 104px;
}

.preview-sticky {
  position: sticky;
  top: 96px;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

/* Actions */
.stepper-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid var(--lulu-border);
}

.primary-btn {
  background: rgb(0, 109, 62) !important;
  color: #ffffff !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  letter-spacing: 0.2px;
  border: none !important;
  border-radius: 999px !important;
  box-shadow: 0 2px 8px rgba(0, 109, 62, 0.2) !important;
  padding: 8px 22px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.primary-btn:hover:not(:disabled) {
  box-shadow: 0 4px 16px rgba(0, 109, 62, 0.3) !important;
  transform: translateY(-1px);
}

.primary-btn:disabled {
  opacity: 0.55;
  box-shadow: none !important;
}

.secondary-btn {
  color: var(--lulu-primary) !important;
  border: 1px solid rgba(0, 109, 62, 0.4) !important;
  background: transparent !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  border-radius: 999px !important;
  padding: 8px 22px !important;
  box-shadow: 0 2px 8px rgba(0, 109, 62, 0.12) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.secondary-btn:hover:not(:disabled) {
  box-shadow: 0 4px 16px rgba(0, 109, 62, 0.2) !important;
  transform: translateY(-1px);
  border-color: var(--lulu-primary) !important;
}

/* Three buttons don't fit side by side on a narrow phone, so stack them
   full width with the primary action on top. */
@media (max-width: 480px) {
  .stepper-actions {
    flex-direction: column-reverse;
    align-items: stretch;
  }
  .stepper-actions :deep(.v-spacer) {
    display: none;
  }
  .stepper-actions .primary-btn,
  .stepper-actions .secondary-btn {
    width: 100%;
  }
}

/* Terms dialog */
.terms-dialog-title {
  background: var(--lulu-header-gradient);
  color: #ffffff;
  font-weight: 700;
  font-size: 18px;
  padding: 20px 24px;
}

.terms-dialog-body {
  padding: 0 !important;
  max-height: 500px;
  overflow-y: auto;
}

.terms-dialog-actions {
  padding: 16px 24px;
  background: var(--lulu-surface-soft);
  border-top: 1px solid var(--lulu-border);
}

/* Submission overlay */
.submit-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.78);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999;
  padding: 20px;
}

.submit-overlay-card {
  background: #ffffff;
  border-radius: 16px;
  padding: 32px 28px;
  max-width: 420px;
  width: 100%;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.submit-overlay-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--lulu-primary);
}

.submit-overlay-message {
  font-size: 14px;
  color: #4a5568;
  line-height: 1.5;
  margin: 0;
}
</style>
